import { useState } from 'react'
import { useNavigate } from 'react-router'
import { motion, useReducedMotion } from 'motion/react'
import { Volume2, VolumeX, BookOpen } from 'lucide-react'
import { useAudio } from '../features/game/AudioContext'
import { useLang } from '../context/LanguageContext'
import { useMatch } from '../features/match/MatchContext'
import { HowToPlayModal } from '../components/game/HowToPlayModal'
import { FEATURES } from '../features/auth/flags'

export default function StartScreen() {
  const navigate = useNavigate()
  const { setConfig } = useMatch()
  const { musicEnabled, toggleMusic } = useAudio()
  const { lang, setLang, t } = useLang()
  const prefersReduced = useReducedMotion() ?? false

  const [quickHover, setQuickHover] = useState(false)
  const [howToPlayOpen, setHowToPlayOpen] = useState(false)

  const handleQuickMatch  = () => { setConfig({ mode: 'quick'  }); navigate('/create') }
  const handleCreateMatch = () => { setConfig({ mode: 'create' }); navigate('/create') }
  const handleJoinMatch   = () => { setConfig({ mode: 'join'   }); navigate('/join')   }

  // Returns motion props for a fade-up entrance, or {} to skip animation
  const fadeUp = (delay: number) => prefersReduced ? {} : {
    initial:    { opacity: 0, y: 18 },
    animate:    { opacity: 1, y: 0 },
    transition: { delay, duration: 0.6, ease: [0.16, 1, 0.3, 1] },
  }

  return (
    <div
      className="relative min-h-screen w-full flex flex-col items-center justify-center overflow-hidden select-none"
      style={{
        // The photo renders on top; the old gradient stays as a layer behind it (shows
        // briefly before the image loads, and backstops the edges if `cover` ever crops
        // the 1536x1024 art oddly), plus a flat backgroundColor as the ultimate fallback.
        backgroundColor: '#1f120b',
        // .webp is a ~15x smaller re-encode of the same art (Home.png, kept on disk as
        // the source) — same visual result, much faster first paint.
        backgroundImage: "url('/images/Home.webp'), radial-gradient(125% 95% at 50% -10%, #3d2415 0%, #1f120b 44%, #100a06 100%)",
        backgroundSize: 'cover, cover',
        backgroundPosition: 'center, center',
        backgroundRepeat: 'no-repeat, no-repeat',
      }}
    >

      {/* ── Atmospheric layers ─────────────────────────────────────────── */}

      {/* Film grain — fractal noise overlay at ~5% */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{ zIndex: 1, mixBlendMode: 'overlay', opacity: 0.055 }}
      >
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <filter id="film-grain">
            <feTurbulence type="fractalNoise" baseFrequency="0.68" numOctaves="3" stitchTiles="stitch" />
            <feColorMatrix type="saturate" values="0" />
          </filter>
          <rect width="100%" height="100%" filter="url(#film-grain)" />
        </svg>
      </div>

      {/* Lamp glow — warm amber bloom from top-center, candle-flickers */}
      <div
        className="candle-glow pointer-events-none absolute inset-x-0 top-0"
        style={{
          height: '65%',
          background: 'radial-gradient(ellipse 55% 75% at 50% -2%, rgba(245,172,14,0.26) 0%, rgba(180,90,15,0.09) 45%, transparent 100%)',
          zIndex: 2,
        }}
      />

      {/* Cigar smoke wisps */}
      <div
        className="smoke-drift-1 pointer-events-none absolute"
        style={{
          left: '36%', bottom: '38%',
          width: 260, height: 320,
          background: 'radial-gradient(ellipse, rgba(215,195,165,0.08) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(26px)',
          zIndex: 2,
        }}
      />
      <div
        className="smoke-drift-2 pointer-events-none absolute"
        style={{
          left: '57%', bottom: '30%',
          width: 190, height: 250,
          background: 'radial-gradient(ellipse, rgba(195,175,145,0.06) 0%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(20px)',
          zIndex: 2,
        }}
      />

      {/* Vignette — edges collapse to near-black */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse 80% 80% at 50% 42%, transparent 25%, rgba(4,2,1,0.50) 62%, rgba(4,2,1,0.93) 100%)',
          zIndex: 3,
        }}
      />

      {/* ── Content stack ─────────────────────────────────────────────── */}
      <div className="relative flex flex-col items-center px-6 pb-4" style={{ zIndex: 10 }}>

        {/* Wordmark — sheen sweep defined in index.css */}
        <motion.div
          {...(prefersReduced ? {} : {
            initial:    { opacity: 0, scale: 0.93 },
            animate:    { opacity: 1, scale: 1 },
            transition: { delay: 0.7, duration: 0.95, ease: [0.16, 1, 0.3, 1] },
          })}
          style={{ filter: 'drop-shadow(0 3px 12px rgba(0,0,0,1)) drop-shadow(0 0 50px rgba(233,205,134,0.18))' }}
        >
          <h1
            className="wordmark-sheen"
            style={{
              fontFamily: "'Cinzel', 'Palatino Linotype', serif",
              fontWeight: 700,
              fontSize: 'clamp(2rem, 6vw, 4rem)',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              lineHeight: 1.1,
              textAlign: 'center',
              marginBottom: '1.1rem',
            }}
          >
            {t('landing.title')}
          </h1>
        </motion.div>

        {/* Deco divider — rule · oxblood diamond · rule */}
        <motion.div
          {...(prefersReduced ? {} : {
            initial:    { opacity: 0, scaleX: 0.3 },
            animate:    { opacity: 1, scaleX: 1 },
            transition: { delay: 1.0, duration: 0.6, ease: 'easeOut' },
          })}
          className="flex items-center gap-3"
          style={{ width: 'min(340px, 82vw)', marginBottom: '2.8rem' }}
        >
          <div style={{ flex: 1, height: 1, background: 'linear-gradient(to right, transparent, rgba(199,154,74,0.65))' }} />
          <div style={{ width: 8, height: 8, transform: 'rotate(45deg)', background: '#b23b2e', flexShrink: 0 }} />
          <div style={{ flex: 1, height: 1, background: 'linear-gradient(to left, transparent, rgba(199,154,74,0.65))' }} />
        </motion.div>

        {/* Quick Match — primary CTA */}
        <motion.div
          {...fadeUp(1.25)}
          className="flex flex-col items-center mb-5"
          style={{ width: 'min(420px, 90vw)' }}
        >
          <button
            onClick={handleQuickMatch}
            onMouseEnter={() => setQuickHover(true)}
            onMouseLeave={() => setQuickHover(false)}
            style={{
              width: '100%',
              padding: '14px 28px',
              background: 'linear-gradient(180deg, #f1d795 0%, #cba24f 100%)',
              color: '#1a0e04',
              border: 'none',
              borderRadius: '6px',
              fontFamily: "'Cinzel', serif",
              fontWeight: 700,
              fontSize: '0.95rem',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              transform: quickHover ? 'translateY(-3px)' : 'translateY(0)',
              boxShadow: quickHover
                ? '0 6px 22px rgba(199,154,74,0.55), 0 2px 8px rgba(0,0,0,0.5)'
                : '0 3px 12px rgba(0,0,0,0.45)',
              transition: 'transform 0.18s ease, box-shadow 0.18s ease',
            }}
          >
            {t('menu.quick')}
          </button>
        </motion.div>

        {/* Ghost pair — Create Match & Join Match (hidden while multiplayer is gated off) */}
        {FEATURES.MULTIPLAYER_ENABLED && (
          <motion.div
            {...fadeUp(1.42)}
            className="flex gap-3"
            style={{ width: 'min(420px, 90vw)' }}
          >
            {([
              { label: t('menu.create'), handler: handleCreateMatch },
              { label: t('menu.join'),   handler: handleJoinMatch   },
            ] as const).map(({ label, handler }) => (
              <button
                key={label}
                onClick={handler}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  // Near-opaque (not fully transparent) so the felt-arc rim line behind this
                  // block can never show through — its position is viewport-height-dependent
                  // and isn't guaranteed to clear the button row on short screens.
                  background: 'rgba(15,8,2,0.92)',
                  color: '#c79a4a',
                  border: '1px solid rgba(199,154,74,0.35)',
                  borderRadius: '6px',
                  fontFamily: "'Cinzel', serif",
                  fontWeight: 600,
                  fontSize: '0.78rem',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  transition: 'border-color 0.18s ease, color 0.18s ease, background 0.18s ease',
                }}
                onMouseEnter={e => {
                  const el = e.currentTarget
                  el.style.borderColor = 'rgba(199,154,74,0.7)'
                  el.style.color = '#e9cd86'
                  el.style.background = 'rgba(35,22,10,0.95)'
                }}
                onMouseLeave={e => {
                  const el = e.currentTarget
                  el.style.borderColor = 'rgba(199,154,74,0.35)'
                  el.style.color = '#c79a4a'
                  el.style.background = 'rgba(15,8,2,0.92)'
                }}
              >
                {label}
              </button>
            ))}
          </motion.div>
        )}
      </div>

      {/* ── Fixed chrome ──────────────────────────────────────────────── */}
      {/* FeedbackButton is mounted globally in App.tsx */}

      {/* Instructions toggle */}
      <button
        onClick={() => setHowToPlayOpen(true)}
        aria-label={t('game.menu.howto')}
        className="fixed bottom-28 right-4 z-50 w-10 h-10 rounded-full flex items-center justify-center"
        style={{
          background: 'rgba(15,8,2,0.85)',
          border: '1px solid rgba(199,154,74,0.27)',
          backdropFilter: 'blur(4px)',
          color: '#c79a4a',
        }}
      >
        <BookOpen className="w-4 h-4" />
      </button>

      {/* Language toggle */}
      <button
        onClick={() => setLang(lang === 'en' ? 'es' : 'en')}
        aria-label={t('layout.lang_toggle')}
        className="fixed bottom-16 right-4 z-50 w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold"
        style={{
          background: 'rgba(15,8,2,0.85)',
          border: '1px solid rgba(199,154,74,0.27)',
          backdropFilter: 'blur(4px)',
          color: '#c79a4a',
        }}
      >
        {lang === 'en' ? '🇪🇸' : '🇺🇸'}
      </button>

      {/* Music toggle */}
      <button
        onClick={toggleMusic}
        aria-label={musicEnabled ? t('layout.mute') : t('layout.unmute')}
        className="fixed bottom-4 right-4 z-50 w-10 h-10 rounded-full flex items-center justify-center"
        style={{
          background: musicEnabled ? 'rgba(20,12,3,0.85)' : 'rgba(15,8,2,0.85)',
          border: `1px solid ${musicEnabled ? 'rgba(199,154,74,0.33)' : 'rgba(199,154,74,0.13)'}`,
          backdropFilter: 'blur(4px)',
          color: musicEnabled ? '#c79a4a' : '#6b4c2a',
        }}
      >
        {musicEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
      </button>

      {howToPlayOpen && (
        <HowToPlayModal onClose={() => setHowToPlayOpen(false)} finalLabelKey="btn.done" />
      )}
    </div>
  )
}
